import {
  LOAD_CONTENT_LIBRARY,
  CONTENT_LIBRARY_LOADED,
  CONTENT_ADD,
  CONTENT_ADD_DONE,
  CONTENT_ADD_ERROR,
  EDIT_CONTENT,
  EDIT_CONTENT_DONE,
  EDIT_CONTENT_ERROR,
  LOAD_CONTENT_LIBRARY_TOPICS,
  CONTENT_LIBRARY_TOPICS_LOADED,
  TOPIC_ADD,
  TOPIC_ADD_DONE,
  TOPIC_ADD_ERROR,
} from "../actions/actionTypes";
import { put, select, take } from "redux-saga/effects";
import ContentLibraryService from "../../services/contentLibraryService";
import { goBack } from "../../history";
import globalCacheService from "../../services/globalCacheService";

export function* loadLibrary() {
  while (true) {
    const { hardReload } = yield take(LOAD_CONTENT_LIBRARY);

    // get current page and load more
    const contentLibrary = yield select((state) => state.contentLibrary);
    // check if loadMore is true or already loading
    // console.log('load statuses', statuses);

    if (
      !contentLibrary.loading &&
      "loadMore" in contentLibrary &&
      !contentLibrary.loadMore
    ) {
      // load more should not fire as loadMore is false
      return false;
    }

    // get page
    const page = hardReload ? 0 : contentLibrary.page || 0;
    // Only use default filter on true first load when no filter exists, otherwise preserve existing filter
    const filter =
      hardReload === "first" && !contentLibrary.filter
        ? { status: "ACTIVE" }
        : contentLibrary.filter;

    console.log("Content library saga - loading with:", {
      hardReload,
      page,
      filter,
      existingFilterKeys: contentLibrary.filter
        ? Object.keys(contentLibrary.filter)
        : "none",
    });

    // start loading more

    // put will set loading flag
    yield put({ type: LOAD_CONTENT_LIBRARY });
    // load statuses
    try {
      const result = yield ContentLibraryService.load(page, filter, hardReload);
      console.log("SAGA RESULT RETURNED ", result);

      yield put({
        type: CONTENT_LIBRARY_LOADED,
        list: result.list,
        loadMore: result.loadMore,
        page: page + 1,
        totalItems: result.totalItems,
        hardReload,
      });
    } catch (error) {
      console.log(error);
      yield put({
        type: CONTENT_LIBRARY_LOADED,
        list: [],
        loadMore: false,
        page: page + 1,
      });
      // console.log('ERROR', error);
    }
  }
}

export function* addLibraryContent() {
  while (true) {
    const action = yield take(CONTENT_ADD);

    try {
      const result = yield ContentLibraryService.add(action.data);
      
      // Clear cache since content was added
      globalCacheService.clearCachesByPrefix('contentLibrary');
      
      yield put({
        type: CONTENT_ADD_DONE,
        data: { libraryId: result.id, libraryName: result.name },
      });
    } catch (error) {
      yield put({ type: CONTENT_ADD_ERROR, error: error.toString() });
    }
  }
}

export function* editLibraryContent() {
  while (true) {
    const action = yield take(EDIT_CONTENT);

    try {
      const result = yield ContentLibraryService.edit(
        action.contentId,
        action.data
      );
      
      // Clear cache since content was edited - the edited item should appear at top of list
      globalCacheService.clearCachesByPrefix('contentLibrary');
      
      yield put({ type: EDIT_CONTENT_DONE });
      
      // Set a flag to indicate we're returning from edit so component knows to reset page
      if (typeof window !== 'undefined') {
        window.isReturningFromEdit = true;
      }
      
      goBack("/content-library/" + action.contentId + "/edit");
    } catch (error) {
      yield put({ type: EDIT_CONTENT_ERROR, error: error.toString() });
    }
  }
}

export function* loadLibraryTopics() {
  while (true) {
    const action = yield take(LOAD_CONTENT_LIBRARY_TOPICS);
    yield put({ type: LOAD_CONTENT_LIBRARY_TOPICS });
    try {
      // Check if we already have topics in the state and they're fresh
      const contentLibrary = yield select((state) => state.contentLibrary);
      const hasTopics = contentLibrary.topics && contentLibrary.topics.length > 0;
      
      if (hasTopics && !action.forceRefresh) {
        // We already have topics, no need to reload
        console.log('Content library saga: Topics already loaded, skipping API call');
        yield put({
          type: CONTENT_LIBRARY_TOPICS_LOADED,
          topics: contentLibrary.topics,
        });
      } else {
        // Load topics with caching
        const result = yield ContentLibraryService.loadTopics(
          action.patientTypeId,
          action.forceRefresh
        );
        console.log('Content library saga: Topics loaded from API/cache');
        yield put({
          type: CONTENT_LIBRARY_TOPICS_LOADED,
          topics: result,
        });
      }
    } catch (error) {
      console.log('Content library saga: Error loading topics', error);
      yield put({
        type: CONTENT_LIBRARY_TOPICS_LOADED,
        topics: [],
      });
    }
  }
}

export function* addTopic() {
  while (true) {
    const action = yield take(TOPIC_ADD);

    try {
      const result = yield ContentLibraryService.addTopic(action.data);
      yield put({ type: TOPIC_ADD_DONE });
      goBack("/content-library/topics");
    } catch (error) {
      yield put({ type: TOPIC_ADD_ERROR, error: error.toString() });
    }
  }
}
